import { InventoryLocation } from "./InventoryLocation";
import TrackedObject from "./TrackedObject";

/**
 * Basis a Certificate of Analysis assay is expressed on. The literals match the values the
 * database constrains `Inventory.AssayBasis` to, so they pass through the API unchanged.
 */
export type AssayBasis = "AsIs" | "Anhydrous" | "Dried";

export const ASSAY_BASES: readonly AssayBasis[] = ["AsIs", "Anhydrous", "Dried"];

export const ASSAY_BASIS_LABELS: Readonly<Record<AssayBasis, string>> = {
    AsIs: "As-Is",
    Anhydrous: "Anhydrous",
    Dried: "Dried",
};

/** Dropdown options for an assay basis select; the blank entry means "no COA basis". */
export const ASSAY_BASIS_OPTIONS: readonly { value: AssayBasis | null; text: string }[] = [
    { value: null, text: "" },
    ...ASSAY_BASES.map(basis => ({ value: basis, text: ASSAY_BASIS_LABELS[basis] })),
];

/**
 * A compound formula in which a drug is a flagged ingredient with a fixed conversion factor, which
 * replaces the lot's COA for that ingredient. Returned by the lot-form lookup so the forms can say
 * that COA typed on the lot will not drive those weigh-outs.
 */
export interface FixedFactorIngredientUsage {
    compoundId: number,
    compoundName: string,
    compoundNdc: string | null,
    fixedConversionFactor: number
}

export interface Inventory extends TrackedObject {
    id: number | null,
    drugSource: number,
    packageId: number,
    billingUnit: string,
    quantity: number,
    storeId: number,
    storeName: string,
    programId: number | string | undefined,
    programName: string,
    allocated: number,
    reason: string | null,
    location: InventoryLocation,
    useForFilling: boolean,
    lotNumber: string | null,
    expirationDate: string | null,
    costPerUnit: number | null,
    dateReceived: string | null,
    status: string | null,
    /** COA assay % for this lot; with assayBasis, the minimum for the lot to count as having COA. */
    assayPercent: number | null,
    assayBasis: AssayBasis | null,
    /** Loss on Drying / Water Content %; not applicable on an As-Is basis. */
    lodPercent: number | null
}

export class Inventory implements Inventory{
    constructor(obj?:Inventory){
        if(obj) Object.assign(this, obj);
    }
}

/**
 * COA held by another inventory row for the same physical lot (drug + lot number), returned by
 * the sibling lookup so a lot form can pre-fill and warn on mismatch.
 */
export interface LotCoaSibling {
    inventoryId: number,
    storeId: number,
    storeName: string | null,
    locationId: number | null,
    expirationDate: string | null,
    assayPercent: number | null,
    assayBasis: AssayBasis | null,
    lodPercent: number | null,
    updated: string,
    updatedBy: string | null
}
